/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file voipDialNumber.js
 * @description
 * Pure (React-free) outgoing-number normalizer for the SIP softphone. Some
 * PBX providers (e.g. KOMPaaS) only route international calls that carry their
 * international prefix (`00…`), so the typed / directory number is rewritten
 * with the backend-supplied `rtc.dialPlan` before the SIP URI is built.
 *
 * Pure JavaScript (ESM), no TypeScript.
 */

/** Numbers with this many digits or fewer are extensions / feature codes. */
const MAX_EXTENSION_DIGITS = 6;
/** Minimum digit count for a bare `<countryCode>…` number to be international. */
const MIN_BARE_INTERNATIONAL_DIGITS = 10;

/**
 * @typedef {object} DialPlan
 * @property {string} [internationalPrefix] Prefix the PBX needs for international calls (e.g. `00`).
 * @property {string} [countryCode] Local country calling code without prefix (e.g. `36`).
 * @property {string} [nationalPrefix] Trunk prefix used for domestic long-distance (e.g. `06`).
 */

/**
 * Rewrite a raw number into the form the PBX expects.
 * @param {string} raw Typed / directory number (any punctuation).
 * @param {(DialPlan|null|undefined)} dialPlan Backend `rtc.dialPlan`.
 * @returns {string} Dialable target (user part of the SIP URI).
 */
export function formatDialTarget(raw, dialPlan) {
    const cleaned = String(raw == null ? '' : raw).replace(/[^\d+*#]/g, '');
    const plus = cleaned.startsWith('+');
    // A `+` is only meaningful as the leading character.
    const stripped = (plus ? '+' : '') + cleaned.replace(/\+/g, '');

    if (!dialPlan || typeof dialPlan !== 'object') {
        return stripped;
    }
    const intl = dialPlan.internationalPrefix ? String(dialPlan.internationalPrefix) : '';
    const country = dialPlan.countryCode ? String(dialPlan.countryCode).replace(/\D/g, '') : '';
    const national = dialPlan.nationalPrefix ? String(dialPlan.nationalPrefix) : '';
    const digits = stripped.replace(/\D/g, '');

    if (!intl || /[*#]/.test(stripped) || digits.length <= MAX_EXTENSION_DIGITS) {
        return stripped;
    }
    if (plus) {
        return intl + digits;
    }
    if (digits.startsWith('00')) {
        return intl + digits.slice(2);
    }
    if (national && country && digits.startsWith(national)) {
        return intl + country + digits.slice(national.length);
    }
    if (country && digits.startsWith(country) && digits.length >= MIN_BARE_INTERNATIONAL_DIGITS) {
        return intl + digits;
    }

    return digits;
}

export default formatDialTarget;
